import { IUserRepository } from "../../adapters/driven/repositories/userRepository";

/**
 * Maps an AWS-wrapped Cognito error string (the `detail.error` value
 * returned by the resend-invite endpoint when the underlying
 * `AdminSetUserPassword` call fails) to a user-friendly message.
 *
 * The string we receive looks like:
 *   "An error occurred (XException) when calling the YOperation: Z"
 *
 * Strategy:
 *   1. Match a known exception name and return a hand-tuned message.
 *   2. Otherwise extract the trailing portion after the last `": "`
 *      so the technician at least sees the real reason rather than
 *      AWS boilerplate.
 *   3. Return `null` if nothing parses — the caller falls back.
 */
function friendlyResendInviteError(awsErr: string): string | null {
  if (!awsErr || typeof awsErr !== "string") return null;

  if (awsErr.includes("PasswordHistoryPolicyViolationException")) {
    return "That password was used recently. Please choose a different one.";
  }
  if (awsErr.includes("InvalidPasswordException")) {
    return "That password doesn't meet the security requirements. Please try a different one.";
  }
  if (awsErr.includes("LimitExceededException")) {
    return "Too many attempts in a short time. Please wait a few minutes and try again.";
  }

  // Generic AWS error: extract everything after the last ": " so the
  // technician sees the actual reason rather than the AWS noise.
  const lastColon = awsErr.lastIndexOf(": ");
  if (lastColon > -1 && lastColon < awsErr.length - 2) {
    const tail = awsErr.slice(lastColon + 2).trim();
    if (tail) return tail;
  }
  return null;
}

export const UserServiceImplFactory = ({
  userRepository,
}: {
  userRepository: IUserRepository;
}) => {
  const getUserBetaFeatures: IGetUserBetaFeatures = async () => {
    try {
      const response = await userRepository.getUserBetaFeatures();
      return {
        success: true,
        data: { data: response.data },
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const updateUserInfo: IUpdateUserInfo = async (payload) => {
    try {
      const response = await userRepository.updateUserInfo(payload);
      if ("detail" in response && typeof response.detail === "string") {
        return { success: false, error: { message: response.detail } };
      }
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const signupUser: ISignupUser = async (payload) => {
    try {
      const response = await userRepository.signupUser(payload);

      // Check if the response is a success (has user_id and string message)
      if ("user_id" in response && typeof response.message === "string") {
        return {
          success: true,
          data: {
            message: response.message,
            user_id: response.user_id,
          },
        };
      }
      // Handle validation error response (has string[] message and data: null)
      else if ("message" in response && Array.isArray(response.message)) {
        return {
          success: false,
          error: {
            message: response.message,
            detail: undefined,
          },
        };
      }
      // Handle detail error response (has detail string)
      else if ("detail" in response && typeof response.detail === "string") {
        return {
          success: false,
          error: {
            message: undefined,
            detail: response.detail,
          },
        };
      }
      // Handle other message formats
      else if ("message" in response && typeof response.message === "string") {
        return {
          success: false,
          error: {
            message: response.message,
            detail: undefined,
          },
        };
      }
      // Fallback for unexpected response format
      else {
        return {
          success: false,
          error: {
            message: "Unexpected response format",
            detail: undefined,
          },
        };
      }
    } catch (error) {
      return {
        success: false,
        error: {
          message: (error as unknown as Error).message,
          detail: undefined,
        },
      };
    }
  };

  const getAdminPendingUsers: IGetAdminPendingUsers = async (payload) => {
    const email = payload.email?.trim() || undefined;
    const phone = payload.phone?.trim() || undefined;
    if (!email && !phone) {
      return {
        success: false,
        error: {
          message:
            "Provide an email or phone to search pending users (required by the API).",
        },
      };
    }
    const limit =
      payload.limit !== undefined
        ? Math.min(200, Math.max(1, Math.floor(payload.limit)))
        : 50;
    const offset =
      payload.offset !== undefined
        ? Math.max(0, Math.floor(payload.offset))
        : 0;
    try {
      const response = await userRepository.getAdminPendingUsers({
        email,
        phone,
        limit,
        offset,
      });
      if (
        response &&
        typeof response === "object" &&
        "total" in response &&
        typeof (response as IAdminPendingUsersList).total === "number" &&
        "results" in response &&
        Array.isArray((response as IAdminPendingUsersList).results)
      ) {
        return {
          success: true,
          data: response as IAdminPendingUsersList,
        };
      }
      const err = response as { detail?: string; message?: string };
      return {
        success: false,
        error: {
          message:
            (typeof err.detail === "string" && err.detail) ||
            (typeof err.message === "string" && err.message) ||
            "Unable to load pending users.",
        },
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const activateAdminPendingUser: IActivateAdminPendingUser = async (
    payload
  ) => {
    try {
      const body: IActivateAdminPendingUserAPIBody = {
        clinic_id: payload.clinic_id,
        date_of_admission: payload.date_of_admission,
      };
      if (payload.dob?.trim()) body.dob = payload.dob.trim();
      if (payload.gender?.trim()) body.gender = payload.gender.trim();

      const response = await userRepository.activateAdminPendingUser({
        cognito_user_id: payload.cognito_user_id,
        body,
      });

      if (response && typeof response === "object") {
        const r = response as Record<string, unknown>;
        if (
          typeof r.clinic_patient_id === "number" &&
          typeof r.user_id === "string"
        ) {
          return {
            success: true,
            data: {
              user_id: r.user_id as string,
              clinic_patient_id: r.clinic_patient_id as number,
              admission_id:
                typeof r.admission_id === "number"
                  ? (r.admission_id as number)
                  : 0,
              message:
                typeof r.message === "string"
                  ? (r.message as string)
                  : "User activated successfully.",
            },
          };
        }
        if (
          typeof r.user_id === "string" &&
          typeof r.message === "string" &&
          r.clinic_patient_id === undefined
        ) {
          return {
            success: true,
            data: {
              user_id: r.user_id as string,
              message: r.message as string,
              alreadyActivated: true,
            },
          };
        }
        const err = response as { detail?: string; message?: string };
        return {
          success: false,
          error: {
            message:
              (typeof err.detail === "string" && err.detail) ||
              (typeof err.message === "string" && err.message) ||
              "Unable to activate user.",
          },
        };
      }
      return {
        success: false,
        error: { message: "Unexpected response from server." },
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const resendUserInvite: IResendUserInvite = async (payload) => {
    const cognitoUserId = payload.cognito_user_id?.trim();
    const password = payload.password;
    const clinicId = payload.clinic_id;
    if (!cognitoUserId) {
      return {
        success: false,
        error: { message: "Missing user identifier." },
      };
    }
    if (!Number.isFinite(clinicId) || clinicId <= 0) {
      return {
        success: false,
        error: { message: "Missing clinic for this user." },
      };
    }
    if (!password) {
      // Defensive — the UI also blocks this. We deliberately do not
      // include the offending value in any log/error path; password
      // material must never leave this scope.
      return {
        success: false,
        error: { message: "Missing new password." },
      };
    }
    try {
      const response = await userRepository.resendUserInvite({
        clinic_id: clinicId,
        cognito_user_id: cognitoUserId,
        body: { password },
      });
      if (response && typeof response === "object") {
        const r = response as {
          message?: string;
          detail?: string | { message?: string; error?: string };
          code?: string;
          invite_warnings?: unknown[];
        };

        // ── Error path ────────────────────────────────────────
        // `detail` arrives as a plain string for permission / lookup
        // errors (per the spec: forbidden, clinic_mismatch, etc.) and
        // as a nested `{ message, error }` object when the underlying
        // Cognito call surfaces an AWS exception (e.g.,
        // PasswordHistoryPolicyViolationException). Handle both.
        if (typeof r.detail === "string" && r.detail.trim()) {
          return {
            success: false,
            error: { message: r.detail, code: r.code },
          };
        }
        if (r.detail && typeof r.detail === "object") {
          const d = r.detail;
          // Prefer a hand-tuned mapping for known Cognito exceptions
          // so the technician sees actionable text ("That password
          // was used recently…") rather than the raw AWS message.
          const friendly =
            typeof d.error === "string"
              ? friendlyResendInviteError(d.error)
              : null;
          return {
            success: false,
            error: {
              message:
                friendly ||
                (typeof d.message === "string" && d.message) ||
                "Could not reset password.",
              code: r.code,
            },
          };
        }

        // ── Success path ──────────────────────────────────────
        // Success responses always include a `message` alongside
        // `invite_warnings` (possibly empty array).
        if (Array.isArray(r.invite_warnings) || typeof r.message === "string") {
          return {
            success: true,
            data: {
              message:
                typeof r.message === "string" && r.message
                  ? r.message
                  : "Invite resent successfully.",
              invite_warnings: Array.isArray(r.invite_warnings)
                ? r.invite_warnings
                : [],
            },
          };
        }

        // Fallthrough — unrecognized shape.
        return {
          success: false,
          error: {
            message:
              (typeof r.message === "string" && r.message) ||
              "Unexpected response from server.",
            code: r.code,
          },
        };
      }
      return {
        success: false,
        error: { message: "Unexpected response from server." },
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const getClinicUsers: IGetClinicUsers = async (payload) => {
    const limit = Math.min(200, Math.max(1, Math.floor(payload.limit ?? 50)));
    const offset = Math.max(0, Math.floor(payload.offset ?? 0));
    try {
      const response = await userRepository.getClinicUsers({
        clinic_id: payload.clinic_id,
        limit,
        offset,
      });
      if (
        response &&
        typeof response === "object" &&
        "data" in response &&
        Array.isArray((response as IClinicUsersListAPIResponse).data) &&
        "total" in response
      ) {
        return { success: true, data: response as IClinicUsersList };
      }
      const err = response as { detail?: string; message?: string };
      return {
        success: false,
        error: {
          message:
            (typeof err.detail === "string" && err.detail) ||
            (typeof err.message === "string" && err.message) ||
            "Unable to load clinic users.",
        },
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  return {
    getUserBetaFeatures,
    updateUserInfo,
    signupUser,
    getAdminPendingUsers,
    activateAdminPendingUser,
    resendUserInvite,
    getClinicUsers,
  };
};
